// Per-person stress feedback, "Harjoitellaan painoa" (Axel's decision of 2026-10-09; docs/PEDAGOGY.md
// §1.13 "feedback", §8). The stress check (audio/stress.ts) judges each word of a take; recording_score
// keeps only the counts, so nothing said which words a person misses. This file keeps the per-word
// results of a take and decides, per person, which words are in focus:
//
// - Only takes measured against Peterson's clip of the same text count (the long-vowel meter's
//   `mode === 'reference'`): his recording is the one model (pronunciation/reference.ts). A take with
//   his average lengths, Harjoitusvauhti or no clip leaves no word result, and an actor's show clip is
//   never a model, so show audio never puts a word in focus.
// - A word (its form: the stress depends on the form, not the dictionary word) enters the focus when it
//   was missed in two separate takes on different days (one bad sitting is not a habit), and leaves it
//   after two on-target takes after its last miss. The same two on-target takes also forget a miss
//   that has not brought the word in yet. A take that says the word twice is one vote: a miss if
//   either was off target.
// - Names keep their own pronunciation and a borrowing may keep its own stress (rules/borrowed-stress.ts):
//   neither is ever a focus word.
//
// The app shows the focus with neutral words only ("Harjoitellaan painoa: X"), never a score or a
// warning colour. Pure: the app passes the takes (server rows + its outbox), the API validates with
// readStressTake.
import { BORROWED_STRESS } from '../rules/borrowed-stress';
import { normalizeHv } from '../normalize';
import type { AnalysisLike } from './logs';

/** The rule as data. */
export const STRESS_FOCUS = {
  /** Takes in which the word was missed before it is in focus. */
  enterMisses: 2,
  /** Those misses fall on different days (the person's local days). */
  enterOnDifferentDays: true,
  /** On-target takes after the last miss that take it out again. */
  leaveHits: 2,
  /** Focus words named on the week's Puhu step (and offered first in "Missä paino?" and the Puhu take). */
  shown: 3,
  /** Word results kept per take at most (a take is a word or a line). */
  maxWords: 40,
  /** Takes the server returns per person (the newest). */
  maxTakes: 2000,
} as const;

/** The only meter model whose takes leave word results: Peterson's clip of the same text. */
export const FOCUS_MODE = 'reference' as const;

export type StressMiss = 'initial' | 'other';

/** One word of a take whose stress the check judged. */
export interface StressWordResult {
  /** Word number in the take's text (0-based, as the stress check counts). */
  index: number;
  /** The word as written, lowercase NFC (the focus key). */
  form: string;
  /** Its dictionary word when the analyser has exactly one; null when not known for sure. */
  lemmaId: string | null;
  onTarget: boolean;
  /** Of a miss: 'initial' = Finnish first-syllable stress. */
  miss: StressMiss | null;
}

/** One analysed take with its word results (stress_word rows joined to their recording_score row). */
export interface StressTake {
  /** recording_score.id */
  id: string;
  /** The recording target ("lemma:ābra", "dialogue:…"). */
  target: string;
  /** The text recorded. */
  hv: string;
  mode: typeof FOCUS_MODE;
  /** YYYY-MM-DD, the person's local day. */
  day: string;
  at: string;
  words: StressWordResult[];
}

/** What the app knows of a word (the analyser's readings). */
export interface FocusWordInfo {
  lemmaId: string | null;
  /** Only proper-noun readings: a name keeps its own pronunciation. */
  name?: boolean;
}

const BORROWED = new Set(BORROWED_STRESS.map((b) => b.word));
const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** The word key: lowercase NFC, letters and apostrophes only. */
export function focusForm(text: string): string {
  return normalizeHv(text)
    .replace(/[^\p{L}\p{M}']/gu, '')
    .normalize('NFC');
}

/**
 * The word results of one analysed take; none unless it was measured against Peterson's clip of the
 * same text. `info` reads a word (the app's analyser: the form, the word as written, its number); without
 * it no lemma is given and no name is recognised.
 */
export function stressWordResults(a: AnalysisLike, info?: (form: string, text: string, index: number) => FocusWordInfo): StressWordResult[] {
  if (a.vowelLength?.mode !== FOCUS_MODE) return [];
  const out: StressWordResult[] = [];
  const words = a.stress?.words ?? [];
  words.forEach((w, i) => {
    if (out.length >= STRESS_FOCUS.maxWords) return;
    if (w.verdict !== 'ok' && w.verdict !== 'wrong') return;
    const form = focusForm(w.text ?? '');
    if (!form || BORROWED.has(form)) return;
    const index = typeof w.index === 'number' && Number.isInteger(w.index) && w.index >= 0 ? w.index : i;
    let known: FocusWordInfo = { lemmaId: null };
    try {
      if (info) known = info(form, w.text ?? form, index);
    } catch {
      known = { lemmaId: null };
    }
    if (known.name) return;
    const onTarget = w.verdict === 'ok';
    out.push({
      index,
      form,
      lemmaId: known.lemmaId || null,
      onTarget,
      miss: onTarget ? null : w.error === 'initial' ? 'initial' : 'other',
    });
  });
  return out;
}

// ---------- the focus ----------

/** A word in a person's focus. */
export interface StressFocusWord {
  form: string;
  lemmaId: string | null;
  /** When it entered the focus (the take that made it). */
  since: string;
  /** The newest take that missed it: practising says that text again, against the same clip of Peterson's. */
  practice: { target: string; hv: string; at: string };
  /** On-target takes since its last miss (it leaves at STRESS_FOCUS.leaveHits). */
  hits: number;
}

interface Track {
  form: string;
  lemmaId: string | null;
  inFocus: boolean;
  missDays: Set<string>;
  misses: number;
  hits: number;
  since: string;
  practice: StressFocusWord['practice'];
}

const order = (a: StressTake, b: StressTake) => a.at.localeCompare(b.at) || a.id.localeCompare(b.id);

/** One person's focus words from their takes (any order), the newest miss first. */
export function stressFocus(takes: readonly StressTake[]): StressFocusWord[] {
  const tracks = new Map<string, Track>();
  const seen = new Set<string>();
  for (const take of [...takes].sort(order)) {
    if (take.mode !== FOCUS_MODE || seen.has(take.id)) continue;
    seen.add(take.id);
    // one vote per word per take: a miss if any of its occurrences missed
    const votes = new Map<string, { onTarget: boolean; lemmaId: string | null }>();
    for (const w of take.words) {
      const was = votes.get(w.form);
      votes.set(w.form, { onTarget: (was?.onTarget ?? true) && w.onTarget, lemmaId: was?.lemmaId ?? w.lemmaId });
    }
    for (const [form, v] of votes) {
      let t = tracks.get(form);
      if (!t) {
        t = {
          form,
          lemmaId: v.lemmaId,
          inFocus: false,
          missDays: new Set(),
          misses: 0,
          hits: 0,
          since: '',
          practice: { target: take.target, hv: take.hv, at: take.at },
        };
        tracks.set(form, t);
      }
      if (!t.lemmaId && v.lemmaId) t.lemmaId = v.lemmaId;
      if (!v.onTarget) {
        t.practice = { target: take.target, hv: take.hv, at: take.at };
        t.hits = 0;
        if (t.inFocus) continue;
        t.misses++;
        t.missDays.add(take.day);
        const enough = t.misses >= STRESS_FOCUS.enterMisses && (!STRESS_FOCUS.enterOnDifferentDays || t.missDays.size >= STRESS_FOCUS.enterMisses);
        if (enough) {
          t.inFocus = true;
          t.since = take.at;
        }
      } else {
        t.hits++;
        if (t.hits >= STRESS_FOCUS.leaveHits) {
          // out again (or a single miss not followed by a second one is forgotten): a new run of
          // misses is needed to come (back) in
          t.inFocus = false;
          t.misses = 0;
          t.missDays = new Set();
          t.hits = 0;
        }
      }
    }
  }
  return [...tracks.values()]
    .filter((t) => t.inFocus)
    .sort((a, b) => b.practice.at.localeCompare(a.practice.at) || a.form.localeCompare(b.form))
    .map((t) => ({ form: t.form, lemmaId: t.lemmaId, since: t.since, practice: t.practice, hits: t.hits }));
}

/** The focus words to name (the Puhu step, the Puhu take): at most STRESS_FOCUS.shown. */
export function shownFocus(focus: readonly StressFocusWord[]): StressFocusWord[] {
  return focus.slice(0, STRESS_FOCUS.shown);
}

// ---------- the course gate ----------

/**
 * The lesson from which the focus is used: the first lesson whose meta says `stressFocus: true` (lesson 1,
 * whose Puhu step is the first recording with the stress check). Null: none.
 */
export function stressFocusFromLesson(metas: readonly { order: number; stressFocus?: boolean }[]): number | null {
  let best: number | null = null;
  for (const m of metas) if (m.stressFocus && (best === null || m.order < best)) best = m.order;
  return best;
}

// ---------- validation (the API, the app's cached copy) ----------

const str = (x: unknown, max: number): string | null => (typeof x === 'string' && x.length > 0 && x.length <= max ? x.normalize('NFC') : null);

/** A word result as sent or stored; null when it is not one. */
export function readStressWord(x: unknown): StressWordResult | null {
  if (!x || typeof x !== 'object') return null;
  const o = x as Record<string, unknown>;
  const index = o.index;
  if (typeof index !== 'number' || !Number.isInteger(index) || index < 0 || index > 199) return null;
  const raw = str(o.form, 80);
  const form = raw ? focusForm(raw) : '';
  if (!form) return null;
  if (typeof o.onTarget !== 'boolean') return null;
  const lemmaId = str(o.lemmaId, 120);
  const miss: StressMiss | null = o.onTarget ? null : o.miss === 'initial' ? 'initial' : 'other';
  return { index, form, lemmaId, onTarget: o.onTarget, miss };
}

/** The words of a take: valid ones only, one per index, at most STRESS_FOCUS.maxWords. */
export function readStressWords(x: unknown): StressWordResult[] {
  if (!Array.isArray(x)) return [];
  const out: StressWordResult[] = [];
  const at = new Set<number>();
  for (const w of x) {
    const r = readStressWord(w);
    if (!r || at.has(r.index)) continue;
    at.add(r.index);
    out.push(r);
    if (out.length >= STRESS_FOCUS.maxWords) break;
  }
  return out;
}

/** A take as the server returns it (or the outbox holds it); null when it is not one. */
export function readStressTake(x: unknown): StressTake | null {
  if (!x || typeof x !== 'object') return null;
  const o = x as Record<string, unknown>;
  const id = str(o.id, 160);
  const target = str(o.target, 200);
  const hv = str(o.hv, 1000);
  const day = typeof o.day === 'string' && DAY.test(o.day) ? o.day : null;
  const at = typeof o.at === 'string' && !Number.isNaN(Date.parse(o.at)) ? o.at : null;
  if (!id || !target || !hv || !day || !at || o.mode !== FOCUS_MODE) return null;
  const words = readStressWords(o.words);
  if (!words.length) return null;
  return { id, target, hv, mode: FOCUS_MODE, day, at, words };
}
